import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ChevronRight, GripVertical, Store } from "lucide-react";
import { observer } from "mobx-react-lite";
import { type FC, useEffect, useState } from "react";

import { CustomButton, DefaultImage, Switch } from "@/components/atoms";
import { toastError } from "@/components/molecules";
import { CurrencySymbol } from "@/components/molecules/CurrencySymbol";
import { useMenuActions } from "@/components/units/BrandFormMenu/menuActions/MenuActionsContext";
import { getBranchScopeLabelFromUnavailable } from "@/components/units/BrandFormMenu/menuMappers";
import {
  type DishFormType,
  getDishMediaPreviewSrc,
  isDishMediaVideo,
  sortDishImagesByOrder,
} from "@/components/units/BrandFormMenu/types";
import { rootStore } from "@/store/RootStore";
import { commonIconProps20 } from "@/utils/constants";
import { formatPrice } from "@/utils/formatPrice";

interface Props {
  item: DishFormType;
  category: string;
  onClick: () => void;
  hasUpdatePermission?: boolean;
}

const SortableDishItem: FC<Props> = observer(
  ({ item, category, onClick, hasUpdatePermission = true }) => {
    const [localActive, setLocalActive] = useState(item.active ?? true);

    const menuStore = rootStore.menuStore;
    const menuActions = useMenuActions();

    const { attributes, listeners, setNodeRef, transform, transition } =
      useSortable({ id: item.id || "" });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
    };

    const handleClick = (id: string) => {
      menuStore.setCurrentDish(category, id);
      onClick();
    };

    useEffect(() => {
      setLocalActive(item.active ?? true);
    }, [item.active]);

    const handleToggle = async (value: boolean) => {
      if (!item.id) return;

      const previous = localActive;
      setLocalActive(value);

      try {
        await menuActions.updateDishActive(item.id, value);
      } catch (error) {
        setLocalActive(previous);
        toastError(
          error instanceof Error
            ? error.message
            : "Failed to update dish status",
          () => {
            handleToggle(value);
          },
          "Error"
        );
      }
    };

    const branchLabel = getBranchScopeLabelFromUnavailable(
      item.unavailableBranchIds,
      menuStore.totalBranches
    );

    const firstMedia = sortDishImagesByOrder(item.images?.media)[0];
    const previewSrc = getDishMediaPreviewSrc(firstMedia);
    const isVideo = isDishMediaVideo(firstMedia);

    return (
      <li
        ref={setNodeRef}
        style={style}
        className="flex items-center gap-[20px] px-2 py-3 border-b border-gray-200"
      >
        <CustomButton
          variant="icon"
          type="button"
          {...attributes}
          {...listeners}
          data-testid={`dish-item-drag-${item.dishNameEnglish}`}
          disabled={hasUpdatePermission}
        >
          <GripVertical {...commonIconProps20} color="#AFAFAF" />
        </CustomButton>

        <div className="w-full flex items-center gap-[32px]">
          <div className="flex flex-col gap-1 w-full">
            <p className="text-sm mr-auto font-bold">{item.dishNameEnglish}</p>
            <div className="flex items-center gap-2">
              <Store className="w-3 h-3 text-gray-400" />
              <p className="text-gray-400 text-xs">{branchLabel}</p>
            </div>
          </div>

          <div
            className="text-sm flex items-center gap-1"
            data-testid={`dish-item-price-${item.dishNameEnglish}`}
          >
            {/* Symbol follows the menu's currency; a Jordanian dish must not
                show a Riyal. */}
            <CurrencySymbol
              code={menuStore.currency?.code}
              symbol={menuStore.currency?.symbol}
            />
            {formatPrice(item.price, menuStore.priceDecimals)}
          </div>

          {previewSrc ? (
            isVideo ? (
              <video
                className="w-12 h-12 rounded-md object-cover flex-shrink-0"
                src={previewSrc}
                muted
                playsInline
                preload="metadata"
                aria-label={item.dishNameEnglish}
              />
            ) : (
              <img
                className="w-12 h-12 rounded-md object-cover flex-shrink-0"
                src={previewSrc}
                alt={item.dishNameEnglish}
              />
            )
          ) : (
            <DefaultImage classes="w-[50px] h-[50px] flex-shrink-0" />
          )}

          <Switch
            checked={localActive}
            onCheckedChange={handleToggle}
            data-testid={`dish-item-switch-${item.dishNameEnglish}`}
            disabled={hasUpdatePermission}
          />
        </div>

        <CustomButton
          variant="icon"
          type="button"
          className="p-0"
          onClick={() => handleClick(item.id || "")}
          data-testid={`dish-item-open-${item.dishNameEnglish}`}
        >
          <ChevronRight {...commonIconProps20} color="#030712" />
        </CustomButton>
      </li>
    );
  }
);

export default SortableDishItem;
